import React, { createContext, useContext, useMemo } from 'react';
import { Easing, interpolate, useCurrentFrame } from 'remotion';
import { PlayRanges } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/PlayRange';

// Number of frames over which to fade in/out at each cut boundary.
// Increase for a softer fade; decrease for a tighter fade.
const PLAY_RANGE_CUT_FADE_FRAMES = 3;

/**
 * Computes a volume multiplier (0–1) that fades audio to silence at internal
 * cut boundaries within a composite segment's playRanges.
 *
 * When parts of video are deleted the remaining playRanges are stitched
 * together, which can cause an audible "click" where the waveform is
 * discontinuous. This multiplier fades out exponentially before each cut
 * and fades back in exponentially after each cut to eliminate the artifact.
 *
 * Note: this crossfade may produce audible artifacts when the source video
 * has embedded background music (common in uploads), since the music
 * waveform will also fade in/out at cut boundaries.
 */
export function getPlayRangeCutFadeMultiplier(
  currentFrame: number,
  playRanges: PlayRanges,
  fadeFrames: number = PLAY_RANGE_CUT_FADE_FRAMES
): number {
  if (playRanges.length === 0) {
    return 1;
  }

  const internalCutBoundaries: number[] = [];
  let cumulativeFrame = 0;
  for (let i = 0; i < playRanges.length; i++) {
    const rangeDuration = playRanges[i].toFrame - playRanges[i].fromFrame;
    if (i > 0) {
      internalCutBoundaries.push(cumulativeFrame);
    }
    cumulativeFrame += rangeDuration;
  }
  const totalPlaybackFrames = cumulativeFrame;

  let multiplier = 1;

  if (currentFrame < fadeFrames) {
    const m = interpolate(currentFrame, [0, fadeFrames], [0, 1], {
      easing: Easing.in(Easing.exp),
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp'
    });
    multiplier = Math.min(multiplier, m);
  }

  const fadeOutStart = totalPlaybackFrames - fadeFrames;
  if (currentFrame >= fadeOutStart) {
    const m = interpolate(currentFrame, [fadeOutStart, totalPlaybackFrames], [1, 0], {
      easing: Easing.out(Easing.exp),
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp'
    });
    multiplier = Math.min(multiplier, m);
  }

  for (const boundary of internalCutBoundaries) {
    const fadeOutStart = boundary - fadeFrames;
    const fadeInEnd = boundary + fadeFrames;

    if (currentFrame >= fadeOutStart && currentFrame < boundary) {
      const m = interpolate(currentFrame, [fadeOutStart, boundary], [1, 0], {
        easing: Easing.out(Easing.exp),
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp'
      });
      multiplier = Math.min(multiplier, m);
    }

    if (currentFrame >= boundary && currentFrame < fadeInEnd) {
      const m = interpolate(currentFrame, [boundary, fadeInEnd], [0, 1], {
        easing: Easing.in(Easing.exp),
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp'
      });
      multiplier = Math.min(multiplier, m);
    }
  }

  return multiplier;
}

/**
 * Precomputes a sparse map of frame → multiplier for only the frames that
 * fall within fade regions. Frames not in the map have multiplier = 1.
 * This avoids recomputing interpolation on every frame.
 */
export function buildPlayRangeCutFadeMap(
  playRanges: PlayRanges,
  fadeFrames: number = PLAY_RANGE_CUT_FADE_FRAMES
): Record<number, number> {
  const map: Record<number, number> = {};
  if (playRanges.length === 0) {
    return map;
  }

  const internalCutBoundaries: number[] = [];
  let cumulativeFrame = 0;
  for (let i = 0; i < playRanges.length; i++) {
    const rangeDuration = playRanges[i].toFrame - playRanges[i].fromFrame;
    if (i > 0) {
      internalCutBoundaries.push(cumulativeFrame);
    }
    cumulativeFrame += rangeDuration;
  }
  const totalPlaybackFrames = cumulativeFrame;

  // Helper: compute multiplier at a specific frame and store the min
  const setMin = (frame: number, value: number): void => {
    map[frame] = map[frame] !== undefined ? Math.min(map[frame], value) : value;
  };

  // Fade in at segment start
  for (let f = 0; f < fadeFrames; f++) {
    const m = interpolate(f, [0, fadeFrames], [0, 1], {
      easing: Easing.in(Easing.exp),
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp'
    });
    setMin(f, m);
  }

  // Fade out at segment end
  const endFadeStart = totalPlaybackFrames - fadeFrames;
  for (let f = endFadeStart; f <= totalPlaybackFrames; f++) {
    const m = interpolate(f, [endFadeStart, totalPlaybackFrames], [1, 0], {
      easing: Easing.out(Easing.exp),
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp'
    });
    setMin(f, m);
  }

  // Fade out/in at each internal cut boundary
  for (const boundary of internalCutBoundaries) {
    // Fade out before the cut
    for (let f = boundary - fadeFrames; f < boundary; f++) {
      const m = interpolate(f, [boundary - fadeFrames, boundary], [1, 0], {
        easing: Easing.out(Easing.exp),
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp'
      });
      setMin(f, m);
    }

    // Fade in after the cut
    for (let f = boundary; f < boundary + fadeFrames; f++) {
      const m = interpolate(f, [boundary, boundary + fadeFrames], [0, 1], {
        easing: Easing.in(Easing.exp),
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp'
      });
      setMin(f, m);
    }
  }

  return map;
}

const PlayRangeCutFadeContext = createContext<number>(1);

export function usePlayRangeCutFade(): number {
  return useContext(PlayRangeCutFadeContext);
}

export function PlayRangeCutFadeProvider({
  playRanges,
  children
}: {
  playRanges: PlayRanges;
  children: React.ReactNode;
}): React.ReactElement {
  const currentFrame = useCurrentFrame();

  // Precompute fade map once when playRanges change (not on every frame)
  const fadeMap = useMemo(() => buildPlayRangeCutFadeMap(playRanges), [playRanges]);

  const multiplier = fadeMap[currentFrame] ?? 1;

  return <PlayRangeCutFadeContext.Provider value={multiplier}>{children}</PlayRangeCutFadeContext.Provider>;
}
